import React, { useCallback, useMemo } from "react";
import { AuthManagerContext } from "../../contexts/AuthManager";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { StackScreenProps } from "@react-navigation/stack";
import VStack from "../../ui_atoms/VStack";
import { useI18n } from "../../hooks/i18n-hooks";
import { Gender, MaritalStatus, PersonInput, WorkingPermit } from "../../__generated__/graphql";
import { useMutation } from "@apollo/client";
import Loading from "../../components/Loading";
import { UPDATE_WORKER_PROFILE } from "../../graphql/core";
import { useApolloErrorSnackbar } from "../../hooks/snackbar";
import { useTheme } from "../../hooks/use-theme";
import TextField from "../../components/TextField";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { XOR } from "ts-essentials";
import SelectField from "../../components/SelectField";
import { isValidAVSNumber } from "../../avs";
import OptionsField from "../../components/OptionsField";
import { BodyMediumText, BodySmallText } from "../../components/FormattedText";
import ScreenCard from "../ScreenCard";
import { avsMask, fromMaybe } from "../../utils";
import { StyleSheet } from "react-native";
import { useCloseEditor } from "./use-close-editor";

type PartnerFormData = Pick<PersonInput, "firstName" | "lastName" | "avsNumber">;

type FormData = {
    avsNumber: string;
    hasOtherActivity: boolean;
} & {
    maritalStatus: MaritalStatus;
    partner?: PartnerFormData &
        XOR<
            { hasRevenue?: false },
            {
                hasRevenue: true;
                monthlySalary: string;
            }
        >;
};

type CivilStateEditorScreenProps = StackScreenProps<RootStackParamList, "CivilStateEdit">;

const CivilStateEditorScreen = ({ navigation, route }: CivilStateEditorScreenProps) => {
    const i18n = useI18n();
    const { colors } = useTheme();
    const { profileId } = route.params || {};
    const { user, refreshUser } = React.useContext(AuthManagerContext);

    const [
        updateWorkerProfile,
        { loading: updateLoading, error: updateError, reset: updateReset },
    ] = useMutation(
        UPDATE_WORKER_PROFILE,
        profileId ? { context: { liftProfile: profileId } } : undefined,
    );

    useApolloErrorSnackbar(
        updateError,
        updateReset,
        i18n.t("screens.profile_editor.civil_state.errors.update_avatar_error"),
    );

    const initialMaritalStatus = useMemo(
        () => user?.workerProfile?.maritalStatus,
        [user?.workerProfile?.maritalStatus],
    );

    const initialHasOtherActivity = useMemo(
        () => fromMaybe(user?.workerProfile?.hasOtherActivity),
        [user?.workerProfile?.hasOtherActivity],
    );

    const workPermit = user?.workPermit;
    const nationality = user?.nationality;

    const {
        handleSubmit,
        control,
        watch,
        formState: { isValid, isDirty },
    } = useForm<FormData>({
        mode: "onTouched",
        reValidateMode: "onChange",
        defaultValues: {
            avsNumber: user?.workerProfile?.avsNumber || undefined,
            maritalStatus: initialMaritalStatus || undefined,
            partner: {
                firstName: user?.workerProfile?.partner?.firstName || undefined,
                lastName: user?.workerProfile?.partner?.lastName || undefined,
                avsNumber: user?.workerProfile?.partner?.avsNumber || undefined,
                ...(user?.workerProfile?.partner?.monthlySalary
                    ? {
                          hasRevenue: true,
                          monthlySalary: (
                              user.workerProfile.partner.monthlySalary / 100
                          ).toString(),
                      }
                    : {}),
            },
            hasOtherActivity: initialHasOtherActivity,
        },
    });

    const maritalStatus = watch("maritalStatus");
    const partnerHasRevenue = watch("partner.hasRevenue");
    const avsNumber = watch("avsNumber");
    const partnerAvs = watch("partner.avsNumber");

    const isForeignerWithoutPermit = useMemo(
        () => nationality !== "CH" && !workPermit,
        [nationality, workPermit],
    );

    const isSwissOrResident = useMemo(
        () => workPermit === WorkingPermit.PermitC || nationality === "CH",
        [nationality, workPermit],
    );

    const requirePartnerData = useMemo(
        () => !isSwissOrResident && maritalStatus === MaritalStatus.Married,
        [isSwissOrResident, maritalStatus],
    );

    const askOtherActivity = useMemo(
        () =>
            workPermit &&
            [WorkingPermit.PermitB, WorkingPermit.PermitL, WorkingPermit.PermitS].includes(
                workPermit,
            ),
        [workPermit],
    );

    const onSubmit = useCallback<SubmitHandler<FormData>>(
        async (data) => {
            try {
                await updateWorkerProfile({
                    variables: {
                        changes: {
                            avsNumber: data.avsNumber,
                            maritalStatus: data.maritalStatus,
                            hasOtherActivity: data.hasOtherActivity,
                            ...(data.maritalStatus === MaritalStatus.Married && requirePartnerData
                                ? {
                                      partner: {
                                          firstName: data.partner?.firstName,
                                          lastName: data.partner?.lastName,
                                          avsNumber: data.partner?.avsNumber,
                                          gender: Gender.Unknown,
                                          monthlySalary: data.partner?.hasRevenue
                                              ? parseInt(data.partner.monthlySalary) * 100
                                              : null, // set it to null to "remove" the value, undefined will remove the property
                                      },
                                  }
                                : { partner: undefined }),
                        },
                    },
                });
                navigation.goBack();
                void refreshUser?.();
            } catch (ex) {
                // console.error(ex);
                // this is handled by updateError in useMutation
            }
        },
        [navigation, refreshUser, requirePartnerData, updateWorkerProfile],
    );

    useCloseEditor(isDirty && !updateLoading);

    if (!user) {
        return (
            <VStack>
                <Loading />
            </VStack>
        );
    }

    return (
        <ScreenCard
            buttons={[
                {
                    title: i18n.t("save"),
                    onPress: handleSubmit(onSubmit),
                    validating: updateLoading,
                    isValid: isValid && isDirty,
                    mode: "secondary",
                },
            ]}
        >
            <VStack h="2/3" gap={1}>
                <Controller
                    control={control}
                    name="avsNumber"
                    rules={{
                        required: !isForeignerWithoutPermit, // AVS is not required for foreigners
                        validate: (v) => {
                            if (isForeignerWithoutPermit && !v) {
                                // allow empty field
                                return true;
                            }
                            if (isValidAVSNumber(v)) {
                                if (!requirePartnerData || v !== partnerAvs) {
                                    return true;
                                } else {
                                    return i18n.t(
                                        "screens.profile_editor.civil_state.errors.not_unique_own_avs",
                                    );
                                }
                            } else {
                                return i18n.t("errors.avs_number");
                            }
                        },
                    }}
                    render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                        <TextField
                            error={error?.message}
                            value={value}
                            label={`${i18n.t("profile.avs_number")}${isForeignerWithoutPermit ? ` (${i18n.t("optional").toLowerCase()})` : ``}`}
                            onChangeText={onChange}
                            onBlur={onBlur}
                            keyboardType="numeric"
                            mask={avsMask}
                            maskAutoComplete
                        />
                    )}
                />
                <>
                    <Controller
                        control={control}
                        name="maritalStatus"
                        rules={{
                            required: true,
                        }}
                        render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                            <SelectField<string>
                                error={error?.message}
                                value={value}
                                style={{
                                    ...(requirePartnerData
                                        ? {
                                              borderBottomRightRadius: 0,
                                              borderBottomLeftRadius: 0,
                                          }
                                        : {}),
                                }}
                                halfScreen
                                data={Object.values(MaritalStatus).map((v) => ({
                                    value: v,
                                    label: i18n.t(`user.${v}`),
                                }))}
                                label={i18n.t("screens.profile_editor.civil_state.civil_state")}
                                onSelect={(e) => {
                                    onChange(e.value);
                                }}
                                onBlur={onBlur}
                            />
                        )}
                    />
                    {requirePartnerData && (
                        <>
                            <Controller
                                control={control}
                                name="partner.firstName"
                                rules={{
                                    required: true,
                                }}
                                render={({
                                    field: { onChange, onBlur, value },
                                    fieldState: { error },
                                }) => {
                                    return (
                                        <TextField
                                            isSelfContained
                                            style={styles.stackedField}
                                            error={error?.message}
                                            value={value || ""}
                                            label={i18n.t(
                                                "screens.profile_editor.civil_state.partner_first_name",
                                            )}
                                            onChangeText={onChange}
                                            onBlur={onBlur}
                                        />
                                    );
                                }}
                            />
                            <Controller
                                control={control}
                                name="partner.lastName"
                                rules={{
                                    required: true,
                                }}
                                render={({
                                    field: { onChange, onBlur, value },
                                    fieldState: { error },
                                }) => {
                                    return (
                                        <TextField
                                            isSelfContained
                                            style={styles.stackedField}
                                            error={error?.message}
                                            value={value || ""}
                                            label={i18n.t(
                                                "screens.profile_editor.civil_state.partner_last_name",
                                            )}
                                            onChangeText={onChange}
                                            onBlur={onBlur}
                                        />
                                    );
                                }}
                            />
                            <Controller
                                control={control}
                                name="partner.avsNumber"
                                rules={{
                                    required: !isForeignerWithoutPermit, // Partner's AVS is not required for foreigners
                                    validate: (v) => {
                                        if (isForeignerWithoutPermit && !v) {
                                            // allow empty field
                                            return true;
                                        }
                                        if (v && isValidAVSNumber(v)) {
                                            if (v !== avsNumber) {
                                                return true;
                                            } else {
                                                return i18n.t(
                                                    "screens.profile_editor.civil_state.errors.not_unique_partner_avs",
                                                );
                                            }
                                        } else {
                                            return i18n.t("errors.avs_number");
                                        }
                                    },
                                }}
                                render={({
                                    field: { onChange, onBlur, value },
                                    fieldState: { error },
                                }) => {
                                    return (
                                        <TextField
                                            isSelfContained
                                            style={{
                                                borderTopRightRadius: 0,
                                                borderTopLeftRadius: 0,
                                                marginTop: -2,
                                            }}
                                            error={error?.message}
                                            value={value || ""}
                                            label={`${i18n.t("screens.profile_editor.civil_state.partner_avs")}${isForeignerWithoutPermit ? ` (${i18n.t("optional").toLowerCase()})` : ``}`}
                                            onChangeText={onChange}
                                            onBlur={onBlur}
                                            keyboardType="numeric"
                                            mask={avsMask}
                                            maskAutoComplete
                                        />
                                    );
                                }}
                            />
                        </>
                    )}
                </>
                {requirePartnerData && (
                    <VStack gap={0.25}>
                        <BodyMediumText>
                            {i18n.t("screens.profile_editor.civil_state.partner_has_revenue")}
                        </BodyMediumText>
                        <Controller
                            control={control}
                            name="partner.hasRevenue"
                            rules={{
                                validate: (v) => v !== undefined,
                            }}
                            render={({
                                field: { onChange, onBlur, value },
                                fieldState: { error },
                            }) => (
                                <OptionsField
                                    value={value}
                                    data={[
                                        { label: i18n.t("yes"), value: true },
                                        { label: i18n.t("no"), value: false },
                                    ]}
                                    onSelect={onChange}
                                    onBlur={onBlur}
                                    error={error?.message}
                                    shrinked
                                />
                            )}
                        />
                    </VStack>
                )}
                {requirePartnerData && partnerHasRevenue && (
                    <VStack gap={0.5}>
                        <Controller
                            control={control}
                            name="partner.monthlySalary"
                            rules={{
                                required: false,
                                validate: (v) => !v || !!Number.isInteger(Number.parseInt(v)),
                            }}
                            render={({
                                field: { onChange, onBlur, value },
                                fieldState: { error },
                            }) => (
                                <TextField
                                    error={error?.message}
                                    left="CHF"
                                    inputStyle={{ textAlign: "right" }}
                                    isDecimalNumber
                                    value={value}
                                    label={i18n.t(
                                        "screens.profile_editor.civil_state.partner_revenue",
                                    )}
                                    onChangeText={onChange}
                                    onBlur={onBlur}
                                    selectTextOnFocus
                                    maxValue={99999}
                                    keyboardType="decimal-pad"
                                    returnKeyType="done"
                                />
                            )}
                        />
                        <BodySmallText color={colors.fieldLabel}>
                            {i18n.t(
                                "screens.profile_editor.civil_state.partner_revenue_description",
                            )}
                        </BodySmallText>
                    </VStack>
                )}
                {askOtherActivity && (
                    <VStack gap={0.25}>
                        <BodyMediumText>
                            {i18n.t("screens.profile_editor.civil_state.has_other_activity")}
                        </BodyMediumText>
                        <Controller
                            control={control}
                            name="hasOtherActivity"
                            rules={{
                                validate: (v) => v !== undefined,
                            }}
                            render={({
                                field: { onChange, onBlur, value },
                                fieldState: { error },
                            }) => (
                                <OptionsField
                                    value={value}
                                    data={[
                                        { label: i18n.t("yes"), value: true },
                                        { label: i18n.t("no"), value: false },
                                    ]}
                                    onSelect={onChange}
                                    onBlur={onBlur}
                                    error={error?.message}
                                    shrinked
                                />
                            )}
                        />
                    </VStack>
                )}
            </VStack>
        </ScreenCard>
    );
};

const styles = StyleSheet.create({
    stackedField: {
        borderTopRightRadius: 0,
        borderTopLeftRadius: 0,
        borderBottomRightRadius: 0,
        borderBottomLeftRadius: 0,
        marginTop: -2,
    },
});

export default CivilStateEditorScreen;
